/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import * as React from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { reportClientError, type ClientErrorKind } from '../lib/clientErrorReport';
import { isChunkLoadError, reloadOnceForStaleBundle } from '../lib/staleBundle';
import errorBoundaryCopy from 'virtual:ere-error-boundary-copy';

/**
 * Copy comes straight from the baseline JSON rather than through `t()`.
 *
 * This boundary is mounted ABOVE LanguageProvider — it exists to catch a crash
 * in the providers themselves, so it cannot call useLanguage(). Reading the
 * baseline keeps the strings in the one place the project keeps strings (no
 * hardcoded copy, no second source to drift) while staying independent of the
 * i18n runtime it may be reporting a crash in.
 *
 * Through a build-time virtual module (scripts/vite-plugin-i18n-baseline.mjs)
 * that carries ONLY the `error_boundary` sub-tree of each file: a static
 * import of the JSON files here pinned both complete baselines into the entry
 * chunk, which is what the language provider stopped shipping on 2026-10-01.
 */
const COPY: Record<'nl' | 'en', Record<string, string>> = errorBoundaryCopy;

type Lang = keyof typeof COPY;

/**
 * Language without context: every public route is language-prefixed, so the
 * path is authoritative. `documentElement.lang` is the fallback for the admin
 * surface, which is not prefixed.
 */
function resolveLanguage(): Lang {
  try {
    const seg = window.location?.pathname?.split('/')[1];
    if (seg === 'nl' || seg === 'en') return seg;
    const docLang = document.documentElement.getAttribute('lang');
    if (docLang === 'en') return 'en';
  } catch {
    /* fall through to the default */
  }
  return 'nl';
}

/**
 * A lazily-imported route whose chunk 404s after a deploy throws here too, and
 * it is not a bug in the page — it is a stale bundle. Tagging it separately
 * keeps the admin error log honest: those entries mean "someone had the old
 * build open", not "this page is broken".
 */
function classify(error: Error): ClientErrorKind {
  return isChunkLoadError(error) ? 'chunk' : 'render';
}

/*
 * A stale bundle is the one crash class that fixes itself, and the reload —
 * once per session, never in the admin or the partner portal — is the shared
 * policy of src/lib/staleBundle.ts, which also reloads on Vite's
 * `vite:preloadError` (installed in main.tsx).
 */

interface Props {
  children?: React.ReactNode;
  /**
   * Rendered in place of the crash card. `null` is a meaningful value — it
   * means "drop this subtree and say nothing", which is the right answer for
   * an optional overlay whose loss a visitor would not notice. Presence is
   * what counts, not truthiness: `fallback={null}` used to fall through to
   * the full-page card, the exact opposite of what it asks for.
   */
  fallback?: React.ReactNode;
  /**
   * Names the subtree in the crash report (`[cookie-banner] …`). Once a
   * boundary absorbs a crash the page looks fine, so the label is the only
   * thing left in the admin error log that says which slot failed — and the
   * only way to tell a contained crash apart from one that took the page down.
   */
  label?: string;
  /**
   * Change this to clear a caught error. The route-level boundary passes the
   * pathname, so navigating away from a broken page recovers instead of
   * leaving the visitor stuck on the error screen until a full reload.
   */
  resetKey?: string | number;
  /**
   * 'page' fills the viewport (root boundary, nothing else is rendered).
   * 'inline' is a card that sits inside an existing layout, so header, footer
   * and navigation survive a crash in one route.
   */
  variant?: 'page' | 'inline';
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public override state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    const kind = classify(error);
    const message = error?.message || String(error);
    // A chunk failure reloads first: when the reload started, the visitor is
    // about to get the current page, so the record is a warning — the chunk
    // may be stale (pre-deploy HTML) or a blip on a phone's connection, and
    // neither is a broken page. A second failure in the session, or one in the
    // admin or the portal, stays a crash.
    const reloading = kind === 'chunk' && reloadOnceForStaleBundle();
    // The old copy promised "we've been notified" while doing exactly this
    // console.error and nothing more. Now it is true.
    reportClientError({
      kind,
      message: this.props.label ? `[${this.props.label}] ${message}` : message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack ?? undefined,
      // A null fallback means "optional overlay, drop it and say nothing":
      // the visitor got a complete page, so the report is a warning, not a
      // crash. The label above still says which slot it was.
      contained: this.props.fallback === null || reloading,
    });
  }

  public override componentDidUpdate(prev: Props) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  public render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    const lang = resolveLanguage();
    const t = COPY[lang];
    // A stale bundle is not a broken page, and telling someone mid-edit that
    // "this page ran into an error" sends them hunting for a bug that isn't
    // there. Name what actually happened and what fixes it.
    const isStale = this.state.error ? classify(this.state.error) === 'chunk' : false;
    const isPage = (this.props.variant ?? 'page') === 'page';
    const isDev = (import.meta as unknown as { env: { DEV: boolean } }).env?.DEV;

    return (
      <div
        role="alert"
        className={
          isPage
            ? 'min-h-screen bg-slate-950 flex items-center justify-center p-4'
            : 'flex items-center justify-center p-4 py-20'
        }
      >
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-10 shadow-2xl text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-red-600" />

          <div className="w-20 h-20 bg-red-900/20 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-red-800/20">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>

          {/* text-fg, not text-white: slate-900 remaps to #f8fafc in light mode,
              where the old white heading and secondary-button label were
              invisible against their own card. */}
          <h2 className="text-2xl font-black text-fg mb-4">{isStale ? t.stale_title : t.title}</h2>
          <p className="text-fg-secondary font-bold leading-relaxed mb-10">
            {isStale ? t.stale_body : t.body}
          </p>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full py-4 bg-emerald-700 hover:bg-emerald-800 text-white font-black rounded-2xl transition-all shadow-xl shadow-emerald-900/20 flex items-center justify-center gap-3"
            >
              <RefreshCw className="w-5 h-5" />
              {t.retry}
            </button>
            {/* Language-prefixed: `/` would bounce a Dutch visitor through the
                root redirect and can land them on the other language. */}
            <a
              href={`/${lang}`}
              className="w-full py-4 bg-slate-800 hover:bg-slate-700 text-fg font-black rounded-2xl transition-all flex items-center justify-center gap-3"
            >
              <Home className="w-5 h-5" />
              {t.home}
            </a>
          </div>

          {isDev && this.state.error && (
            <div className="mt-8 pt-8 border-t border-slate-800 text-left">
              <p className="field-label mb-2">{t.details}</p>
              <pre className="bg-slate-950 p-4 rounded-xl text-xs text-red-400 overflow-auto max-h-40 font-mono">
                {this.state.error.message}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }
}
